import { useState } from "react";
import type { HTMLAttributes } from "react";
import "./Avatar.css";

export type AvatarSize = "sm" | "md" | "lg";

export interface AvatarProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** Used for the initials, the tint and the accessible name. */
  name: string;
  src?: string;
  size?: AvatarSize;
  /** Adds the 8px online dot, bottom-right. */
  presence?: boolean;
  /** Hides the avatar from assistive tech (the name is already nearby). */
  decorative?: boolean;
}

/** Five token-backed tints. No random hex, no per-render variation. */
const TINT_COUNT = 5;

function tintIndex(name: string): number {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }
  return hash % TINT_COUNT;
}

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  const result = `${first}${last}`.toUpperCase();
  return result.length > 0 ? result : "?";
}

export function Avatar({
  name,
  src,
  size = "md",
  presence = false,
  decorative = false,
  className,
  ...rest
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = src !== undefined && src.length > 0 && !failed;

  const classes = [
    "fc-avatar",
    `fc-avatar--${size}`,
    showImage ? null : `fc-avatar--tint-${String(tintIndex(name))}`,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <span
      {...rest}
      className={classes}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? true : undefined}
    >
      {showImage ? (
        <img
          className="fc-avatar__image"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="fc-avatar__initials" aria-hidden="true">
          {initialsOf(name)}
        </span>
      )}
      {presence ? (
        <span className="fc-avatar__presence" aria-hidden="true" />
      ) : null}
    </span>
  );
}

export interface AvatarStackItem {
  name: string;
  src?: string;
}

export interface AvatarStackProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  avatars: AvatarStackItem[];
  /** How many faces to show before the `+N` chip. Defaults to 4. */
  max?: number;
  size?: AvatarSize;
}

/**
 * Overlapping faces with a `+N` chip for the remainder. The whole stack has
 * one accessible name listing everybody, so screen readers hear the group
 * rather than a run of individual images.
 */
export function AvatarStack({
  avatars,
  max = 4,
  size = "sm",
  className,
  ...rest
}: AvatarStackProps) {
  const visible = avatars.slice(0, Math.max(0, max));
  const overflow = avatars.length - visible.length;

  const classes = ["fc-avatar-stack", `fc-avatar-stack--${size}`, className]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  const label =
    avatars.length === 0
      ? "No members"
      : `${String(avatars.length)} members: ${avatars.map((entry) => entry.name).join(", ")}`;

  return (
    <div {...rest} className={classes} role="group" aria-label={label}>
      {visible.map((entry) => (
        <Avatar
          key={entry.name}
          name={entry.name}
          src={entry.src}
          size={size}
          decorative
          className="fc-avatar-stack__item"
        />
      ))}
      {overflow > 0 ? (
        <span className="fc-avatar-stack__overflow" aria-hidden="true">
          {`+${String(overflow)}`}
        </span>
      ) : null}
    </div>
  );
}
